import dayjs, { Dayjs } from "dayjs";
import { FilterValue } from "../../Filters/FilterValue";
import { SortType } from "../../SortType";
import {
  CDR_FIELD_OPTIONS,
  DATE_FIELD_OPTIONS,
  INWARD_DATE_FIELD_OPTIONS,
  DEFAULT_INVENTORY_DISPATCH_BUCKET,
  DISPATCH_BUCKET_OPTIONS,
  DailyTasksSortBy,
  DailyTasksStage,
  FILE_WORK_STATE_OPTIONS,
  MOCKUP_WORK_BUCKET_OPTIONS,
  MOCKUP_WORK_STATE_OPTIONS,
  PO_WORK_STATE_OPTIONS,
  PR_WORK_STATE_OPTIONS,
  RAW_WORK_STATE_OPTIONS,
  PRINT_WORK_STATE_OPTIONS,
  PRINT_IN_WORK_STATE_OPTIONS,
  resolveDailyTasksStage,
} from "./DailyTasks";
import { DailyTasksTatAnalyticsBody } from "./DailyTasksTat";

/** null = Both; false = Active; true = Archived (projects.archived). */
export type DailyTasksProjectStatus = boolean | null;

export interface DailyTasksFilters {
  start_date: Dayjs;
  end_date: Dayjs;
  date_field: FilterValue[];
  include_completed: boolean;
  search: string;
  my_tasks: boolean;
  order_type_id: FilterValue[];
  order_id: FilterValue[];
  product_id: FilterValue[];
  customer_id: FilterValue[];
  warehouse_id: FilterValue[];
  pm_poc_id: FilterValue[];
  sales_poc_id: FilterValue[];
  unassigned_pm: boolean;
  customisation_required: FilterValue[];
  dispatch_bucket: FilterValue[];
  mockup_work_state: FilterValue[];
  work_bucket: FilterValue[];
  excluded_orders: boolean;
  project_status: DailyTasksProjectStatus;
  printed_added: FilterValue[];
  print_type_added: FilterValue[];
  print_cost_added: FilterValue[];
  stage: DailyTasksStage;
  page_no: number;
  limit: number;
  sort_by: DailyTasksSortBy;
  sort_type: SortType;
  include_running: boolean;
}

export const PROJECT_STATUS_OPTIONS: FilterValue[] = [
  { id: "both", value: "Both" },
  { id: "active", value: "Active" },
  { id: "archived", value: "Archived" },
];

export const EXCLUDED_FROM_SOURCING_OPTIONS: FilterValue[] = [
  { id: "false", value: "No" },
  { id: "true", value: "Yes" },
];

export const INCLUDE_RUNNING_OPTIONS: FilterValue[] = [
  { id: "false", value: "No" },
  { id: "true", value: "Yes" },
];

export function projectStatusToOption(
  status: DailyTasksProjectStatus,
): FilterValue[] {
  if (status === true) {
    return PROJECT_STATUS_OPTIONS.filter((item) => item.id === "archived");
  }
  if (status === null) {
    return PROJECT_STATUS_OPTIONS.filter((item) => item.id === "both");
  }
  return PROJECT_STATUS_OPTIONS.filter((item) => item.id === "active");
}

export function parseProjectStatusParam(
  raw: string | null,
): DailyTasksProjectStatus {
  if (raw === "both" || raw === "null") return null;
  if (raw === "archived" || raw === "true") return true;
  if (raw === "active" || raw === "false") return false;
  return false;
}

export function projectStatusToParam(
  status: DailyTasksProjectStatus,
): string {
  if (status === true) return "archived";
  if (status === null) return "both";
  return "active";
}

const parseStage = (value: string | null): DailyTasksStage => {
  return resolveDailyTasksStage(value);
};

const parseFilterValues = (
  searchParams: URLSearchParams,
  key: string,
  options?: readonly { id: string; value: string }[],
): FilterValue[] => {
  const raw = searchParams.get(key);
  if (!raw) return [];
  return raw
    .split(",")
    .filter(Boolean)
    .map((id) => {
      const match = options?.find((option) => option.id === id);
      return { id, value: match?.value ?? id };
    });
};

const parseSingle = (
  searchParams: URLSearchParams,
  key: string,
  fallback: FilterValue[],
  options?: readonly { id: string; value: string }[],
): FilterValue[] => {
  const parsed = parseFilterValues(searchParams, key, options);
  return parsed.length > 0 ? parsed : fallback;
};

export const defaultDailyTasksFilters = (): DailyTasksFilters => ({
  start_date: dayjs().subtract(29, "day").startOf("day"),
  end_date: dayjs().endOf("day"),
  date_field: [{ id: "start_date", value: "Project approved" }],
  include_completed: false,
  search: "",
  my_tasks: false,
  order_type_id: [],
  order_id: [],
  product_id: [],
  customer_id: [],
  warehouse_id: [],
  pm_poc_id: [],
  sales_poc_id: [],
  unassigned_pm: false,
  customisation_required: [{ id: "all", value: "All" }],
  dispatch_bucket: [],
  mockup_work_state: [],
  work_bucket: [],
  excluded_orders: true,
  project_status: false,
  printed_added: [{ id: "all", value: "All" }],
  print_type_added: [{ id: "all", value: "All" }],
  print_cost_added: [{ id: "all", value: "All" }],
  stage: "mockup_upload",
  page_no: 1,
  limit: 30,
  sort_by: "mockup_priority",
  sort_type: SortType.ASC,
  include_running: true,
});

export function parseQueryParamsForDailyTasksFilters(
  searchParams: URLSearchParams,
): DailyTasksFilters {
  const defaults = defaultDailyTasksFilters();
  const sortTypeParam = searchParams.get("sort_type");
  const sortByParam = searchParams.get("sort_by");
  const stage = parseStage(searchParams.get("stage"));
  const hasUrlDates = Boolean(searchParams.get("start_date"));
  const parsedWorkBucket = parseFilterValues(
    searchParams,
    "work_bucket",
    MOCKUP_WORK_BUCKET_OPTIONS,
  );
  const parsedDispatchBucket = parseFilterValues(
    searchParams,
    "dispatch_bucket",
    DISPATCH_BUCKET_OPTIONS,
  );
  const reconciled = reconcileDispatchAndWorkBuckets(
    stage,
    hasUrlDates
      ? parsedDispatchBucket
      : usesSlaWorkQueue(stage)
        ? []
        : [...DEFAULT_INVENTORY_DISPATCH_BUCKET],
    hasUrlDates ? parsedWorkBucket : [],
  );

  return {
    start_date: searchParams.get("start_date")
      ? dayjs(searchParams.get("start_date"), "YYYY-MM-DD")
      : defaults.start_date,
    end_date: searchParams.get("end_date")
      ? dayjs(searchParams.get("end_date"), "YYYY-MM-DD")
      : defaults.end_date,
    date_field: parseSingle(
      searchParams,
      "date_field",
      defaults.date_field,
      [...DATE_FIELD_OPTIONS, ...INWARD_DATE_FIELD_OPTIONS],
    ),
    include_completed: false,
    search: searchParams.get("search") || "",
    my_tasks: false,
    order_type_id: parseFilterValues(searchParams, "order_type_id"),
    order_id: parseFilterValues(searchParams, "order_id"),
    product_id: parseFilterValues(searchParams, "product_id"),
    customer_id: parseFilterValues(searchParams, "customer_id"),
    warehouse_id: parseFilterValues(searchParams, "warehouse_id"),
    pm_poc_id: parseFilterValues(searchParams, "pm_poc_id"),
    sales_poc_id: parseFilterValues(searchParams, "sales_poc_id"),
    unassigned_pm: false,
    customisation_required: defaults.customisation_required,
    dispatch_bucket: reconciled.dispatch_bucket,
    mockup_work_state: parseFilterValues(
      searchParams,
      "mockup_work_state",
      [...MOCKUP_WORK_STATE_OPTIONS, ...FILE_WORK_STATE_OPTIONS, ...PR_WORK_STATE_OPTIONS, ...PO_WORK_STATE_OPTIONS, ...RAW_WORK_STATE_OPTIONS, ...PRINT_WORK_STATE_OPTIONS, ...PRINT_IN_WORK_STATE_OPTIONS],
    ),
    work_bucket: reconciled.work_bucket,
    excluded_orders: searchParams.get("excluded_orders") !== "false",
    project_status: parseProjectStatusParam(searchParams.get("project_status")),
    printed_added: parseSingle(
      searchParams,
      "printed_added",
      defaults.printed_added,
      CDR_FIELD_OPTIONS,
    ),
    print_type_added: parseSingle(
      searchParams,
      "print_type_added",
      defaults.print_type_added,
      CDR_FIELD_OPTIONS,
    ),
    print_cost_added: parseSingle(
      searchParams,
      "print_cost_added",
      defaults.print_cost_added,
      CDR_FIELD_OPTIONS,
    ),
    stage,
    page_no: searchParams.get("page_no")
      ? Number(searchParams.get("page_no")) || defaults.page_no
      : defaults.page_no,
    limit: searchParams.get("limit")
      ? Number(searchParams.get("limit")) || defaults.limit
      : defaults.limit,
    sort_by:
      sortByParam &&
      [
        "dispatch_date",
        "start_date",
        "days_to_dispatch",
        "stage_tat_hours",
        "total_tat_hours",
        "mockup_upload_tat_hours",
        "mockup_approval_tat_hours",
        "order_id",
        "brand_name",
        "product_name",
        "pm_poc",
        "sales_poc",
        "mockup_priority",
        "file_priority",
        "pr_priority",
        "po_priority",
        "raw_priority",
        "print_priority",
        "print_in_priority",
        "sla_status",
        "mockup_work_state",
      ].includes(sortByParam)
        ? (sortByParam as DailyTasksSortBy)
        : stage === "file_upload" || stage === "cdr_to_printer"
          ? "file_priority"
          : stage === "pr_creation"
            ? "pr_priority"
            : stage === "po_creation"
              ? "po_priority"
              : stage === "raw_inward" || stage === "raw_inward_qc"
                ? "raw_priority"
                : stage === "print_outward"
                  ? "print_priority"
                  : stage === "print_inward" || stage === "print_inward_qc"
                    ? "print_in_priority"
                    : stage === "mockup_upload" || stage === "mockup_approval"
                      ? "mockup_priority"
                      : "start_date",
    sort_type:
      sortTypeParam === SortType.ASC || sortTypeParam === SortType.DESC
        ? sortTypeParam
        : defaults.sort_type,
    include_running: searchParams.get("include_running") !== "false",
  };
}

const UPCOMING_DISPATCH_BUCKETS = new Set([
  "next_3_days",
  "days_4_to_7",
  "days_7_plus",
]);

const isOverdueTodayWorkQueue = (workBucket: FilterValue[]): boolean => {
  const ids = new Set(workBucket.map((item) => String(item.id)));
  return ids.size === 2 && ids.has("overdue") && ids.has("today");
};

function usesSlaWorkQueue(stage: DailyTasksStage): boolean {
  return (
    stage === "mockup_upload" ||
    stage === "file_upload" ||
    stage === "pr_creation" ||
    stage === "po_creation" ||
    stage === "raw_inward" ||
    stage === "print_outward" ||
    stage === "print_inward"
  );
}

export function reconcileDispatchAndWorkBuckets(
  stage: DailyTasksStage,
  dispatchBucket: FilterValue[],
  workBucket: FilterValue[],
): { dispatch_bucket: FilterValue[]; work_bucket: FilterValue[] } {
  if (!usesSlaWorkQueue(stage)) {
    return {
      dispatch_bucket:
        dispatchBucket.length > 0
          ? dispatchBucket
          : [...DEFAULT_INVENTORY_DISPATCH_BUCKET],
      work_bucket: [],
    };
  }

  const hasUpcomingDispatch = dispatchBucket.some((item) =>
    UPCOMING_DISPATCH_BUCKETS.has(String(item.id)),
  );

  if (hasUpcomingDispatch) {
    return { dispatch_bucket: dispatchBucket, work_bucket: [] };
  }

  if (isOverdueTodayWorkQueue(workBucket)) {
    return {
      dispatch_bucket:
        dispatchBucket.length > 0
          ? dispatchBucket
          : [...DEFAULT_INVENTORY_DISPATCH_BUCKET],
      work_bucket: [],
    };
  }

  const urgency = workBucket.filter((item) => {
    const id = String(item.id);
    return id === "today" || id === "overdue";
  });

  return {
    dispatch_bucket: dispatchBucket,
    work_bucket: urgency.length === 1 ? urgency : [],
  };
}

export function dailyTasksFiltersToSearchParams(
  filters: DailyTasksFilters,
): URLSearchParams {
  const nextParams = new URLSearchParams();
  nextParams.set("start_date", filters.start_date.format("YYYY-MM-DD"));
  nextParams.set("end_date", filters.end_date.format("YYYY-MM-DD"));
  nextParams.set("stage", filters.stage);
  nextParams.set("page_no", String(filters.page_no));
  nextParams.set("limit", String(filters.limit));
  nextParams.set("sort_by", filters.sort_by);
  nextParams.set("sort_type", filters.sort_type);

  const appendList = (key: string, values: FilterValue[]) => {
    if (values.length > 0) {
      nextParams.set(key, values.map((item) => item.id).join(","));
    }
  };

  appendList("date_field", filters.date_field);
  appendList("order_type_id", filters.order_type_id);
  appendList("order_id", filters.order_id);
  appendList("product_id", filters.product_id);
  appendList("customer_id", filters.customer_id);
  appendList("warehouse_id", filters.warehouse_id);
  appendList("pm_poc_id", filters.pm_poc_id);
  appendList("sales_poc_id", filters.sales_poc_id);
  appendList("dispatch_bucket", filters.dispatch_bucket);
  appendList("mockup_work_state", filters.mockup_work_state);
  appendList("work_bucket", filters.work_bucket);
  appendList("printed_added", filters.printed_added);
  appendList("print_type_added", filters.print_type_added);
  appendList("print_cost_added", filters.print_cost_added);

  if (filters.search.trim()) nextParams.set("search", filters.search.trim());
  nextParams.set(
    "include_running",
    filters.include_running ? "true" : "false",
  );
  nextParams.set(
    "excluded_orders",
    filters.excluded_orders ? "true" : "false",
  );
  nextParams.set("project_status", projectStatusToParam(filters.project_status));

  return nextParams;
}

export function toDailyTasksQueueSearchParams(
  filters: DailyTasksFilters,
  extras?: { forSummary?: boolean },
): URLSearchParams {
  const params = dailyTasksFiltersToSearchParams(filters);
  params.delete("include_running");
  if (extras?.forSummary) {
    params.delete("page_no");
    params.delete("limit");
    params.delete("sort_by");
    params.delete("sort_type");
    params.set("cards_only", "true");
  }
  return params;
}

const filterValuesToIds = (items: FilterValue[]): number[] =>
  items
    .map((item) => Number(item.id))
    .filter((id) => Number.isInteger(id) && id > 0);

export function toDailyTasksTatBody(
  filters: DailyTasksFilters,
): DailyTasksTatAnalyticsBody {
  const warehouseId = filters.warehouse_id[0]?.id;
  const body: DailyTasksTatAnalyticsBody = {
    start_date: filters.start_date.format("YYYY-MM-DD"),
    end_date: filters.end_date.format("YYYY-MM-DD"),
    excluded_orders: filters.excluded_orders,
    project_status: filters.project_status,
    include_running: filters.include_running,
  };

  const orderTypeIds = filterValuesToIds(filters.order_type_id);
  if (orderTypeIds.length > 0) body.order_type_id = orderTypeIds;

  const orderIds = filterValuesToIds(filters.order_id);
  if (orderIds.length > 0) body.order_id = orderIds;

  const productIds = filterValuesToIds(filters.product_id);
  if (productIds.length > 0) body.product_id = productIds;

  const customerIds = filterValuesToIds(filters.customer_id);
  if (customerIds.length > 0) body.customer_id = customerIds;

  const pmIds = filterValuesToIds(filters.pm_poc_id);
  if (pmIds.length > 0) body.pm_poc_id = pmIds;

  const salesIds = filterValuesToIds(filters.sales_poc_id);
  if (salesIds.length > 0) body.sales_poc_id = salesIds;

  if (warehouseId && warehouseId !== "all") {
    body.warehouse_id = String(warehouseId);
  }

  if (filters.search.trim()) {
    body.search = filters.search.trim();
  }

  body.stage = filters.stage;

  const dateFieldId = filters.date_field[0]?.id;
  if (dateFieldId) {
    body.date_field = dateFieldId;
  }

  return body;
}
